import React, { useState } from 'react';
import { Button } from '@rocco/components/button';
import { EllipsisIcon } from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';

export interface ItemMenuAction {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    type: 'action';
    disabled?: boolean;
}

export interface ItemMenuSeparator {
    type: 'separator';
}

interface ItemMenuProps {
    isPopoverOpen?: boolean;
    setIsPopoverOpen?: (isPopoverOpen: boolean) => void;
    actions: (ItemMenuAction | ItemMenuSeparator)[];
}

/**
 * ItemMenu component that displays a popover menu with actions.
 * Supports disabled states for actions to prevent user interaction when needed.
 *
 * @param isPopoverOpen - External control for popover open state
 * @param setIsPopoverOpen - External setter for popover open state
 * @param actions - Array of menu actions and separators
 * @returns The ItemMenu component
 */
export const ItemMenu = ({
    isPopoverOpen: isPopoverOpenProp,
    setIsPopoverOpen: setIsPopoverOpenProp,
    actions,
}: ItemMenuProps) => {
    const [isPopoverOpenInternal, setIsPopoverOpenInternal] = useState(false);

    const isPopoverOpen = isPopoverOpenProp ?? isPopoverOpenInternal;
    const setIsPopoverOpen = setIsPopoverOpenProp ?? setIsPopoverOpenInternal;

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
                <Button
                    color="white"
                    size="square42"
                    aria-label="Menu"
                    title="Menu"
                    className="transition-all duration-100 hover:border-gray-200 hover:bg-white"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisIcon className="h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                paddings="xsmall"
                variant="white"
                background="white"
            >
                <div className="flex min-w-60 flex-col space-y-2">
                    {actions.map((action, index) => {
                        if (action.type === 'separator') {
                            return (
                                <div
                                    key={`separator-${index}`}
                                    className="h-px w-full bg-gray-200"
                                />
                            );
                        }
                        return (
                            <button
                                key={`${action.label}-${index}`}
                                className={`group flex items-center justify-start space-x-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                                    action.disabled
                                        ? 'cursor-not-allowed opacity-50'
                                        : 'cursor-pointer hover:bg-gray-100'
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!action.disabled) {
                                        action.onClick();
                                    }
                                }}
                                disabled={action.disabled}
                                aria-disabled={action.disabled}
                            >
                                {action.icon}
                                {action.label}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
};
